import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {TranscriptionQuota} from '../../store/selectors/usage.selectors';
import {formatDuration, formatDurationCompact} from '../../lib/audioMetadata';
import {useCountdown} from '../useCountdown';

const TONE = {
  ok: 'border-gray-800 bg-gray-900 text-gray-300',
  low: 'border-amber-500/40 bg-amber-500/[0.08] text-amber-200 hover:bg-amber-500/[0.14]',
  critical: 'border-red-500/40 bg-red-500/[0.08] text-red-200 hover:bg-red-500/[0.14]',
} as const;

/**
 * The transcription balance, always visible — and the meters behind it, one click away.
 *
 * Transcription is the only thing in the product that runs out, and the desktop app never showed it
 * — users found the ceiling by hitting a 402. Keeping it next to the project switcher means the
 * cost of the next action is on screen before the action is taken, and the amber/red states are the
 * warning rather than a modal that interrupts.
 *
 * **The face of the pill is what can be started right now, not the period balance.** It used to
 * read `Transcription remaining: 25h`, which was a promise the product no longer keeps: the period
 * allowance is now throttled by a session budget, so 25h left this period can mean 4h — or 40
 * minutes — startable this minute. Leading with the balance sent users into a 429 they had every
 * reason to read as a bug. The balance is still there, as the second half of the same sentence,
 * because it is what an upgrade decision is made on.
 *
 * **An idle session shows no clock.** With `isActive: false` the user has the full budget and their
 * timer has not started — reading usage does not start it, only transcribing does — so the panel
 * says what will happen rather than counting down to a deadline nobody is under.
 */
const QuotaPill: React.FC<{quota: TranscriptionQuota}> = ({quota}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {session} = quota;

  // Only ever non-null while a session is open — `expiresAt` is null when it is not.
  const secondsUntilReset = useCountdown(session?.isActive ? session.expiresAt : null);

  // A popover in the header sits over the page, so it closes on the two gestures that mean "I'm
  // done with it" — anywhere else, or Escape.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!quota.isKnown) {
    return null;
  }

  const {
    secondsRemaining,
    secondsUsed,
    secondsLimit,
    maxFileSeconds,
    periodEnd,
    startableSeconds,
  } = quota;

  const amount = (seconds: number | null) =>
    (seconds === null ? 'Unlimited' : formatDurationCompact(seconds));

  const resetLabel = periodEnd && !Number.isNaN(new Date(periodEnd).getTime())
    ? new Date(periodEnd).toLocaleDateString(undefined, {day: 'numeric', month: 'short'})
    : null;

  // A plan with no throttle has one meter, so naming two would be noise — it keeps the old sentence.
  const hasThrottle = session !== null;

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        title={
          hasThrottle
            ? 'Transcription time — what you can start now, and what is left this period'
            : 'Transcription time — click for the full breakdown'
        }
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs
         transition-colors cursor-pointer ${TONE[quota.displayLevel]}`}
      >
        {hasThrottle ? (
          <>
            <span className="hidden sm:inline opacity-70">Can start now:</span>
            <span className="font-medium tabular-nums">{amount(startableSeconds)}</span>
            <span className="hidden md:inline opacity-60 tabular-nums">
              · {amount(secondsRemaining)} left this period
            </span>
          </>
        ) : (
          <>
            <span className="hidden sm:inline opacity-70">Transcription remaining:</span>
            <span className="font-medium tabular-nums">{amount(secondsRemaining)}</span>
            {maxFileSeconds !== null && (
              <span className="hidden md:inline opacity-60 tabular-nums">
                · {formatDurationCompact(maxFileSeconds)} per file
              </span>
            )}
          </>
        )}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Transcription time"
          className="absolute left-0 top-full z-50 mt-2 w-80 rounded-xl border border-gray-800
           bg-gray-900 p-3 shadow-xl shadow-black/40"
        >
          <p className="text-xs font-medium text-gray-200">Transcription time</p>

          <dl className="mt-2.5 space-y-2.5">
            {hasThrottle && (
              <QuotaRow
                term="Can start now"
                value={amount(startableSeconds)}
                detail={
                  quota.isSessionBound
                    ? 'Limited by your session, not your plan — see below.'
                    : 'The most you can send in one go right now.'
                }
                tone={quota.displayLevel === 'ok' ? 'default' : quota.displayLevel}
              />
            )}

            <QuotaRow
              term="Left this period"
              value={amount(secondsRemaining)}
              detail={
                secondsLimit === null
                  ? 'Your plan has no limit for the billing period.'
                  : `${formatDurationCompact(secondsUsed)} of ${formatDurationCompact(secondsLimit)} used`
                    + (resetLabel ? ` · resets ${resetLabel}` : '')
              }
              tone={quota.level === 'ok' ? 'default' : quota.level}
            />

            {session && (
              <QuotaRow
                term="This session"
                // An idle session's budget is stated as the whole thing, because that is what is
                // waiting. Quoting `secondsRemaining` would be the same number but reads as a
                // remainder — as though something had already been spent.
                value={formatDurationCompact(
                  session.isActive ? session.secondsRemaining : session.secondsLimit
                )}
                detail={
                  session.isActive
                    ? `${formatDurationCompact(session.secondsUsed)} of `
                      + `${formatDurationCompact(session.secondsLimit)} used`
                      + (secondsUntilReset !== null
                        ? ` · resets in ${formatDuration(secondsUntilReset)}`
                        : '')
                    : 'Not started. Your next transcription opens a '
                      + `${formatDurationCompact(session.durationSeconds)} session with this much `
                      + 'audio in it.'
                }
                tone={quota.sessionLevel === 'ok' ? 'default' : quota.sessionLevel}
              />
            )}

            <QuotaRow
              term="Longest single file"
              value={amount(maxFileSeconds)}
              detail={
                maxFileSeconds === null
                  ? 'Any file your plan can hold.'
                  : 'Each file must be under this on its own, however much time you have left.'
              }
            />
          </dl>

          {/* The session running low is a wait, and no plan shortens it — so a "See plans" button
              in that state would be selling a fix for something else. Only the period meter, which
              an upgrade genuinely raises, changes this from neutral to a prompt. */}
          {quota.level !== 'ok' ? (
            <Link
              to="/account"
              onClick={() => setIsOpen(false)}
              className="mt-3 block rounded-lg bg-indigo-600 px-3 py-1.5 text-center text-xs
               font-medium text-white hover:bg-indigo-500 transition-colors"
            >
              See plans
            </Link>
          ) : (
            <>
              {quota.sessionLevel !== 'ok' && (
                <p className="mt-3 text-xs leading-snug text-gray-500">
                  Your plan is fine — this is just the session pacing. Waiting for the reset costs
                  nothing.
                </p>
              )}
              <Link
                to="/account"
                onClick={() => setIsOpen(false)}
                className="mt-3 block rounded-lg border border-gray-800 px-3 py-1.5 text-center
                 text-xs font-medium text-gray-300 hover:bg-gray-800 hover:text-white
                 transition-colors"
              >
                Manage plan
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const VALUE_TONE = {
  default: 'text-gray-100',
  low: 'text-amber-300',
  critical: 'text-red-300',
} as const;

const QuotaRow: React.FC<{
  term: string;
  value: string;
  detail: string;
  tone?: keyof typeof VALUE_TONE;
}> = ({term, value, detail, tone = 'default'}) => (
  <div>
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-gray-400">{term}</dt>
      <dd className={`text-sm font-medium tabular-nums ${VALUE_TONE[tone]}`}>{value}</dd>
    </div>
    <p className="mt-0.5 text-xs leading-snug text-gray-500">{detail}</p>
  </div>
);

export default QuotaPill;
